/**
 * DeadlineTimeline
 * -----------------------------------------------------------------------------
 * Visual, at-a-glance chronological view of every dated event across the
 * whole shortlist:
 *
 *   • Application opens/closes            (purple  #4C1D95)
 *   • Decision-by                         (navy    #002147)
 *   • Deposit due                         (emerald #047857)
 *   • Scholarship deadlines               (amber   #B45309)
 *
 * Complements DeadlineTracker (per-degree list + reminders); the timeline is
 * the "no student ever misses a window" scan surface — sits above the
 * tracker on /shortlist so users see the whole year in one glance before
 * drilling in.
 *
 * Range: today − 30d → latest event + 60d (or +12 months if the shortlist
 * has no dated events yet). All rendering is pure SVG so the timeline
 * stays crisp at every screen width and doesn't need Recharts.
 */
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { filterScholarshipsByLevel } from "@/utils/scholarshipLevel";
import {
  Calendar, GraduationCap, Award, Home, ScrollText,
  Sparkles, ArrowRight, GitCompare, X,
} from "lucide-react";
import { courseTuitionUsd } from "@/lib/courseTuition";

const EVENT_TYPES = {
  application_open: {
    label: "Applications open",
    colour: "#4C1D95",
    ring: "#EDE9FE",
    Icon: ScrollText,
  },
  application_close: {
    label: "Application deadline",
    colour: "#7C3AED",
    ring: "#EDE9FE",
    Icon: ScrollText,
  },
  decision: {
    label: "Decision by",
    colour: "#002147",
    ring: "#E0E7FF",
    Icon: GraduationCap,
  },
  deposit: {
    label: "Deposit due",
    colour: "#047857",
    ring: "#D1FAE5",
    Icon: Home,
  },
  scholarship: {
    label: "Scholarship deadline",
    colour: "#B45309",
    ring: "#FEF3C7",
    Icon: Award,
  },
};

/**
 * WUA+ intent map — each dot type has a specific way our paid counsellors
 * can help. The focus panel surfaces exactly one contextual suggestion
 * so it reads as helpful rather than as an ad. Deep-links to
 * `/wua-plus?intent=…` so the booking form can prefill.
 */
const WUA_PLUS_HINT = {
  application_open: {
    label: "Get a personal-statement blueprint before you start",
    intent: "essay-blueprint",
  },
  application_close: {
    label: "Have a WUA+ coach review your essay before you submit",
    intent: "essay-review",
  },
  decision: {
    label: "Weigh multiple offers with a WUA+ counsellor",
    intent: "offer-review",
  },
  deposit: {
    label: "Not sure about accepting? Book an offer-decision call",
    intent: "offer-decision",
  },
  scholarship: {
    label: "Sharpen your scholarship application with a WUA+ mentor",
    intent: "scholarship-review",
  },
};

function parseISODate(str) {
  if (!str || typeof str !== "string") return null;
  const m = str.match(/(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return null;
  const d = new Date(`${m[1]}-${m[2]}-${m[3]}T00:00:00Z`);
  return isNaN(d.getTime()) ? null : d;
}

function daysUntil(d) {
  if (!d) return null;
  return Math.round((d.getTime() - Date.now()) / 86_400_000);
}

function fmtDate(d) {
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "2-digit" });
}

function fmtMonth(d) {
  return d.toLocaleDateString("en-GB", { month: "short", year: "2-digit" }).replace(" ", "\u00A0");
}

function extractEvents(items) {
  const out = [];
  for (const it of items || []) {
    const deg = it.degree;
    const uni = it.university;
    if (!deg) continue;
    const uniName = uni?.name || it.university_name || "";
    const degTitle = deg.official_title || "";

    // Application events
    const app = deg.application || {};
    const appPairs = [
      ["opens", "application_open"],
      ["closes", "application_close"],
      ["decision_by", "decision"],
      ["deposit_deadline", "deposit"],
    ];
    let closeAdded = false;
    for (const [field, type] of appPairs) {
      const d = parseISODate(app[field]);
      if (d) {
        if (type === "application_close") closeAdded = true;
        out.push({
          id: `${deg.id}::${type}`,
          date: d,
          type,
          university: uniName,
          degree: degTitle,
          degree_id: deg.id,
          university_id: uni?.id || it.university_id || "",
          // Keep the raw degree + uni references so the compare drawer
          // can render tuition / offer_rate / country without a second
          // network trip.
          _degree: deg,
          _uni: uni,
          extra: null,
        });
      }
    }
    // Fallback: if the nested application block didn't yield an
    // application-close marker, use the canonical top-level
    // `application_deadline` ISO (populated on ~97% of degrees by
    // the country-specific backfills).
    if (!closeAdded && deg.application_deadline) {
      const d = parseISODate(deg.application_deadline);
      if (d) {
        out.push({
          id: `${deg.id}::application_close`,
          date: d,
          type: "application_close",
          university: uniName,
          degree: degTitle,
          degree_id: deg.id,
          university_id: uni?.id || it.university_id || "",
          _degree: deg,
          _uni: uni,
          extra: null,
        });
      }
    }

    // Scholarship deadlines (uni-level catalogue is the canonical source).
    // Filter by the enclosing degree's academic level so a Masters-only
    // application deadline doesn't show as a marker on an undergrad
    // shortlist entry.
    const scholarships = filterScholarshipsByLevel(
      uni?.scholarships_available || [],
      deg.qualification,
      deg.duration_years,
    );
    for (const s of scholarships) {
      const d = parseISODate(s.deadline);
      if (!d) continue;
      out.push({
        id: `${deg.id}::sch::${s.name}`,
        date: d,
        type: "scholarship",
        university: uniName,
        degree: degTitle,
        degree_id: deg.id,
        university_id: uni?.id || it.university_id || "",
        _degree: deg,
        _uni: uni,
        extra: s.name,
      });
    }
  }
  return out.sort((a, b) => a.date - b.date);
}

export default function DeadlineTimeline({ items }) {
  const [activeTypes, setActiveTypes] = useState(
    () => new Set(Object.keys(EVENT_TYPES))
  );
  const [focus, setFocus] = useState(null);
  // Compare mode: multi-select up to 2 dots (one per degree) then render
  // a side-by-side offer breakdown. Set of degree_ids so a user can
  // toggle any of the ~4 dots per degree without stacking duplicates.
  const [compareMode, setCompareMode] = useState(false);
  const [compareIds, setCompareIds] = useState([]);

  const events = useMemo(() => extractEvents(items), [items]);
  const visible = useMemo(
    () => events.filter((e) => activeTypes.has(e.type)),
    [events, activeTypes]
  );

  // Degrees available for compare = distinct degree_ids that produced ≥1
  // dated event. If ≤1 is available, hide the compare toggle entirely.
  const compareCandidates = useMemo(() => {
    const seen = new Map();
    for (const e of events) {
      if (!seen.has(e.degree_id)) seen.set(e.degree_id, e);
    }
    return [...seen.values()];
  }, [events]);
  const canCompare = compareCandidates.length >= 2;

  const toggleCompareId = (degreeId) => {
    setCompareIds((prev) => {
      if (prev.includes(degreeId)) return prev.filter((x) => x !== degreeId);
      if (prev.length >= 2) return [prev[1], degreeId];
      return [...prev, degreeId];
    });
  };
  const exitCompare = () => {
    setCompareMode(false);
    setCompareIds([]);
  };
  const selectedForCompare = compareIds
    .map((id) => compareCandidates.find((e) => e.degree_id === id))
    .filter(Boolean);

  // Range: from today − 30d to latest event + 60d, min-clamped to 6 months
  // so short shortlists don't render a tiny 2-inch timeline.
  const range = useMemo(() => {
    const today = new Date();
    today.setUTCHours(0, 0, 0, 0);
    const start = new Date(today.getTime() - 30 * 86_400_000);
    let end;
    if (visible.length > 0) {
      const latest = visible[visible.length - 1].date;
      end = new Date(latest.getTime() + 60 * 86_400_000);
    } else {
      end = new Date(today.getTime() + 365 * 86_400_000);
    }
    // Clamp to at least 6-month span for readability.
    const minEnd = new Date(start.getTime() + 180 * 86_400_000);
    if (end < minEnd) end = minEnd;
    return { start, end, today };
  }, [visible]);

  // Month tick positions (first-of-month strictly between start and end).
  const monthTicks = useMemo(() => {
    const ticks = [];
    const cursor = new Date(range.start);
    cursor.setUTCDate(1);
    cursor.setUTCMonth(cursor.getUTCMonth() + 1);
    while (cursor <= range.end) {
      ticks.push(new Date(cursor));
      cursor.setUTCMonth(cursor.getUTCMonth() + 1);
    }
    return ticks;
  }, [range]);

  const toggleType = (t) => {
    setActiveTypes((prev) => {
      const next = new Set(prev);
      if (next.has(t)) next.delete(t);
      else next.add(t);
      return next;
    });
  };

  // Position an event 0..1 across the range.
  const posOf = (d) => {
    const span = range.end.getTime() - range.start.getTime();
    return Math.max(0, Math.min(1, (d.getTime() - range.start.getTime()) / span));
  };

  const nextThree = visible.filter((e) => daysUntil(e.date) >= 0).slice(0, 3);

  return (
    <section
      data-testid="deadline-timeline"
      className="mt-6 rounded-2xl border border-stone-200 bg-white p-5 sm:p-6"
    >
      <header className="flex items-start justify-between flex-wrap gap-3 mb-4">
        <div className="flex items-start gap-2">
          <Calendar className="h-5 w-5 text-[#4C1D95] mt-0.5" aria-hidden />
          <div>
            <div className="label-uppercase text-[#4C1D95]">
              Guided timeline
            </div>
            <h3 className="mt-0.5 font-editorial text-xl text-[#002147] leading-tight">
              Every deadline across your shortlist
            </h3>
            <p className="mt-1 text-[12px] text-stone-600 max-w-lg">
              One horizontal view of application, scholarship, decision and
              deposit deadlines for every pinned course. Click a dot to see
              the exact date and which course it belongs to.
            </p>
            {canCompare && (
              <button
                type="button"
                onClick={() => {
                  if (compareMode) exitCompare();
                  else {
                    setCompareMode(true);
                    setFocus(null);
                  }
                }}
                data-testid="deadline-timeline-compare-toggle"
                className={
                  "mt-3 inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-[11px] font-underground-sc tracking-wider transition-colors " +
                  (compareMode
                    ? "bg-[#4C1D95] text-white border-[#4C1D95]"
                    : "border-[#4C1D95]/40 text-[#4C1D95] hover:bg-[#4C1D95]/[0.06]")
                }
              >
                {compareMode ? (
                  <>
                    <X className="h-3 w-3" /> Exit compare
                  </>
                ) : (
                  <>
                    <GitCompare className="h-3 w-3" /> Compare two offers
                  </>
                )}
              </button>
            )}
          </div>
        </div>
        {nextThree.length > 0 && !compareMode && (
          <div className="text-right">
            <div className="label-uppercase text-stone-500">Next up</div>
            <div className="mt-1 space-y-0.5 text-[12px] text-stone-700">
              {nextThree.map((e) => {
                const days = daysUntil(e.date);
                const cfg = EVENT_TYPES[e.type];
                return (
                  <div
                    key={e.id}
                    data-testid={`deadline-timeline-nextup-${e.type}`}
                    className="flex items-center justify-end gap-2"
                  >
                    <span
                      className="h-1.5 w-1.5 rounded-full"
                      style={{ background: cfg.colour }}
                    />
                    <span className="tabular-nums">{fmtDate(e.date)}</span>
                    <span className="text-stone-500">
                      · {days === 0 ? "today" : `in ${days}d`}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </header>

      {/* Legend / filters */}
      <div className="flex flex-wrap gap-2 mb-4" data-testid="deadline-timeline-legend">
        {Object.entries(EVENT_TYPES).map(([type, cfg]) => {
          const on = activeTypes.has(type);
          const count = events.filter((e) => e.type === type).length;
          return (
            <button
              key={type}
              type="button"
              onClick={() => toggleType(type)}
              data-testid={`deadline-timeline-filter-${type}`}
              className={
                "inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-[11px] font-underground-sc tracking-wider transition-colors " +
                (on
                  ? "text-stone-900"
                  : "text-stone-400 border-stone-200 bg-stone-50 line-through")
              }
              style={
                on
                  ? { borderColor: cfg.colour, background: cfg.ring }
                  : undefined
              }
            >
              <span
                className="h-2 w-2 rounded-full"
                style={{ background: on ? cfg.colour : "#D6D3D1" }}
              />
              {cfg.label} <span className="opacity-60">· {count}</span>
            </button>
          );
        })}
      </div>

      {/* Timeline surface */}
      {visible.length === 0 ? (
        <div
          className="rounded-xl border border-dashed border-stone-300 bg-stone-50 p-6 text-center text-[13px] text-stone-500"
          data-testid="deadline-timeline-empty"
        >
          No dated deadlines yet for your pinned courses.
          Add scholarship or application dates on each degree page — they'll
          drop onto the timeline automatically.
        </div>
      ) : (
        <>
          {compareMode && (
            <div
              className="mb-3 rounded-lg border border-[#4C1D95]/30 bg-[#F5F0FF] px-3 py-2 text-[12px] text-[#4C1D95] flex items-center gap-2"
              data-testid="deadline-timeline-compare-hint"
            >
              <GitCompare className="h-3.5 w-3.5" />
              <span>
                {selectedForCompare.length === 0
                  ? "Pick 2 dots on the timeline to compare those two offers."
                  : selectedForCompare.length === 1
                  ? "Pick 1 more dot to see the side-by-side."
                  : `Comparing ${selectedForCompare[0].university} vs ${selectedForCompare[1].university}.`}
              </span>
            </div>
          )}
          <TimelineSurface
            range={range}
            monthTicks={monthTicks}
            events={visible}
            posOf={posOf}
            focus={focus}
            setFocus={setFocus}
            compareMode={compareMode}
            compareIds={compareIds}
            onToggleCompare={toggleCompareId}
          />
          {compareMode && selectedForCompare.length === 2 && (
            <ComparePanel
              a={selectedForCompare[0]}
              b={selectedForCompare[1]}
              onExit={exitCompare}
            />
          )}
        </>
      )}
    </section>
  );
}

function TimelineSurface({
  range, monthTicks, events, posOf, focus, setFocus,
  compareMode, compareIds, onToggleCompare,
}) {
  // ── Tube-map layout ────────────────────────────────────────────
  // 5 possible deadline TYPES → 5 possible horizontal lines. Only
  // draw a line for types that actually have at least one event in
  // the current shortlist (so an empty type doesn't burn a row).
  const activeTypes = useMemo(() => {
    const seen = new Set(events.map((e) => e.type));
    // Keep the EVENT_TYPES insertion order so lines always render
    // in a stable top-to-bottom sequence, matching the legend.
    return Object.keys(EVENT_TYPES).filter((t) => seen.has(t));
  }, [events]);
  const LANE_GAP = 32;             // vertical space per tube (was 22)
  const LANE_TOP = 20;             // space above the first tube
  const LANE_BOTTOM = 34;          // space under the last tube for today label
  const yOf = useMemo(() => {
    const map = new Map();
    activeTypes.forEach((t, i) => map.set(t, LANE_TOP + i * LANE_GAP));
    return map;
  }, [activeTypes]);
  const TRACK_HEIGHT = Math.max(
    LANE_TOP + activeTypes.length * LANE_GAP + LANE_BOTTOM,
    96,
  );

  // Bucket events by (type, ISO-date) so multiple entries on the
  // same day for the same line collapse into one station + ×N chip.
  const stationBuckets = useMemo(() => {
    const groups = new Map();       // key: `${type}|${YYYY-MM-DD}`
    for (const e of events) {
      const key = `${e.type}|${e.date.toISOString().slice(0, 10)}`;
      if (!groups.has(key)) groups.set(key, { type: e.type, date: e.date, items: [] });
      groups.get(key).items.push(e);
    }
    return [...groups.values()];
  }, [events]);

  const todayPct = posOf(range.today) * 100;

  return (
    <div className="relative" data-testid="deadline-timeline-surface">
      {/* Month labels row */}
      <div className="relative h-5 mb-2 text-[10px] font-underground-sc tracking-wider text-stone-500">
        {monthTicks.map((m) => (
          <span
            key={m.toISOString()}
            className="absolute -translate-x-1/2 whitespace-nowrap"
            style={{ left: `${posOf(m) * 100}%` }}
          >
            {fmtMonth(m)}
          </span>
        ))}
      </div>

      {/* Line-name legend — one chip per active tube, above the
          track. Chips are the source of truth for tube colour; the
          rail no longer carries in-line labels (they collided with
          stations on mobile). Clicking a chip is a no-op today but
          keeps the affordance discoverable. */}
      {activeTypes.length > 0 && (
        <div
          className="mb-2 flex flex-wrap gap-x-3 gap-y-1"
          data-testid="deadline-timeline-legend"
        >
          {activeTypes.map((t) => {
            const cfg = EVENT_TYPES[t];
            return (
              <span
                key={t}
                className="inline-flex items-center gap-1.5 text-[10px] font-underground-sc tracking-wider text-stone-600"
                data-testid={`deadline-timeline-legend-${t}`}
              >
                <span
                  className="inline-block h-2.5 w-2.5 rounded-full"
                  style={{ background: cfg.colour }}
                  aria-hidden="true"
                />
                {cfg.label}
              </span>
            );
          })}
          <span className="ml-auto text-[10px] font-underground-sc tracking-wider text-stone-400">
            ×N = multiple courses share a date
          </span>
        </div>
      )}

      {/* Tube-map track — SVG, weight-6 lines, round caps, one per
          deadline TYPE. Labels live in the legend above (not inline)
          so mobile stays readable. */}
      <div
        className="relative rounded-lg bg-stone-50 border border-stone-100 overflow-hidden"
        style={{ height: TRACK_HEIGHT }}
      >
        <svg
          viewBox={`0 0 1000 ${TRACK_HEIGHT}`}
          preserveAspectRatio="none"
          className="absolute inset-0 w-full h-full"
          aria-hidden="true"
        >
          {/* Month gridlines */}
          {monthTicks.map((m) => (
            <line
              key={m.toISOString()}
              x1={posOf(m) * 1000}
              x2={posOf(m) * 1000}
              y1={0}
              y2={TRACK_HEIGHT}
              stroke="#E7E5E4"
              strokeWidth={1}
            />
          ))}
          {/* Today gridline — subtle purple */}
          <line
            x1={todayPct * 10}
            x2={todayPct * 10}
            y1={0}
            y2={TRACK_HEIGHT}
            stroke="#4C1D95"
            strokeOpacity={0.35}
            strokeWidth={1}
            strokeDasharray="4 3"
          />
          {/* One tube per active deadline type. `vectorEffect
              non-scaling-stroke` keeps the weight-6 look consistent
              even though we're stretching a fixed viewBox to
              container width. */}
          {activeTypes.map((t) => {
            const y = yOf.get(t);
            const cfg = EVENT_TYPES[t];
            return (
              <line
                key={t}
                x1={0}
                x2={1000}
                y1={y}
                y2={y}
                stroke={cfg.colour}
                strokeOpacity={0.28}
                strokeWidth={6}
                strokeLinecap="round"
                vectorEffect="non-scaling-stroke"
              />
            );
          })}
          {/* Station circles per (type, date) bucket. When the bucket
              has more than one event, the circle grows and the count
              renders INSIDE the disc — no external chip collision. */}
          {stationBuckets.map((b) => {
            const cx = posOf(b.date) * 1000;
            const cy = yOf.get(b.type);
            const cfg = EVENT_TYPES[b.type];
            const activeHere = focus && b.items.some((e) => e.id === focus.id);
            const multi = b.items.length > 1;
            const r = multi ? 10 : (activeHere ? 8 : 7);
            return (
              <g key={`${b.type}-${b.date.toISOString()}`}>
                <circle
                  cx={cx}
                  cy={cy}
                  r={r}
                  fill={cfg.colour}
                  stroke="#ffffff"
                  strokeWidth={2}
                  vectorEffect="non-scaling-stroke"
                />
                {multi && (
                  <text
                    x={cx}
                    y={cy}
                    textAnchor="middle"
                    dominantBaseline="central"
                    fill="#ffffff"
                    style={{ font: "600 10px monospace" }}
                  >
                    {b.items.length}
                  </text>
                )}
                {activeHere && (
                  <circle
                    cx={cx}
                    cy={cy}
                    r={r + 3}
                    fill="none"
                    stroke={cfg.colour}
                    strokeOpacity={0.35}
                    strokeWidth={4}
                    vectorEffect="non-scaling-stroke"
                  >
                    <animate
                      attributeName="r"
                      values={`${r + 3};${r + 7};${r + 3}`}
                      dur="1.6s"
                      repeatCount="indefinite"
                    />
                    <animate
                      attributeName="stroke-opacity"
                      values="0.35;0;0.35"
                      dur="1.6s"
                      repeatCount="indefinite"
                    />
                  </circle>
                )}
              </g>
            );
          })}
          {/* Today station — pulsing purple halo, matches JourneyRail */}
          <g>
            <circle cx={todayPct * 10} cy={TRACK_HEIGHT - 8} r={3.5} fill="#4C1D95" />
            <circle
              cx={todayPct * 10}
              cy={TRACK_HEIGHT - 8}
              r={5}
              fill="none"
              stroke="#4C1D95"
              strokeOpacity={0.35}
              strokeWidth={2}
              vectorEffect="non-scaling-stroke"
            >
              <animate attributeName="r" values="5;9;5" dur="1.6s" repeatCount="indefinite" />
              <animate attributeName="stroke-opacity" values="0.35;0;0.35" dur="1.6s" repeatCount="indefinite" />
            </circle>
          </g>
        </svg>

        {/* "today" text pill above the today gridline — sits BELOW
            the tubes so it never overlaps a station. */}
        <span
          className="absolute whitespace-nowrap rounded-full bg-[#4C1D95] px-1.5 py-px text-[9px] font-underground-sc tracking-wider text-white -translate-x-1/2"
          style={{ left: `${todayPct}%`, bottom: 4 }}
          data-testid="deadline-timeline-today-marker"
        >
          today
        </span>

        {/* Interactive click layer — one absolutely-positioned button
            per station bucket, sized bigger than the drawn circle so
            fingers on mobile can hit it comfortably. NO ×N chip here
            (that lives inside the station disc now). */}
        <div className="absolute inset-0">
          {stationBuckets.map((b) => {
            const leftPct = posOf(b.date) * 100;
            const yPct = (yOf.get(b.type) / TRACK_HEIGHT) * 100;
            const primary = b.items[0];
            const activeHere = focus && b.items.some((e) => e.id === focus.id);
            const selectedForCompare = compareMode
              && b.items.some((e) => compareIds.includes(e.degree_id));
            const onClick = () => {
              if (compareMode) {
                onToggleCompare(primary.degree_id);
              } else {
                setFocus(activeHere ? null : primary);
              }
            };
            const cfg = EVENT_TYPES[b.type];
            const countHint = b.items.length > 1 ? ` +${b.items.length - 1} more` : "";
            return (
              <button
                key={`btn-${b.type}-${b.date.toISOString()}`}
                type="button"
                onClick={onClick}
                data-testid={`deadline-timeline-dot-${b.type}`}
                data-selected-compare={selectedForCompare || undefined}
                aria-label={`${cfg.label} · ${primary.university} · ${fmtDate(b.date)}${countHint}`}
                title={`${cfg.label} · ${primary.university} · ${fmtDate(b.date)}`}
                className="absolute -translate-x-1/2 -translate-y-1/2 flex items-center justify-center rounded-full outline-none focus-visible:ring-2 focus-visible:ring-[#4C1D95]/40"
                style={{
                  left: `${leftPct}%`,
                  top: `${yPct}%`,
                  width: 26,
                  height: 26,
                  background: "transparent",
                }}
              />
            );
          })}
        </div>
      </div>

      {/* Focus panel — same as before, but when a station bucket has
          more than one event we list all of them so the user can pick. */}
      {focus && (
        <FocusPanel
          focus={focus}
          setFocus={setFocus}
          buckets={stationBuckets}
        />
      )}
    </div>
  );
}


/**
 * FocusPanel — extracted so we can render the "N events on this
 * date + line" siblings underneath the primary focus row without
 * bloating TimelineSurface.
 */
function FocusPanel({ focus, setFocus, buckets }) {
  const cfg = EVENT_TYPES[focus.type];
  const key = `${focus.type}|${focus.date.toISOString().slice(0, 10)}`;
  const bucket = buckets.find(
    (b) => `${b.type}|${b.date.toISOString().slice(0, 10)}` === key,
  );
  const siblings = (bucket?.items || []).filter((e) => e.id !== focus.id);
  const hint = WUA_PLUS_HINT[focus.type];
  return (
    <div
      className="mt-3 rounded-xl border border-stone-200 bg-white p-3 shadow-sm"
      data-testid="deadline-timeline-focus"
    >
      <div className="flex items-start gap-3">
        <span
          className="mt-1 inline-block h-2.5 w-2.5 rounded-full shrink-0"
          style={{ background: cfg.colour }}
        />
        <div className="min-w-0 flex-1">
          <div className="label-uppercase" style={{ color: cfg.colour }}>
            {cfg.label}
            {focus.extra ? ` · ${focus.extra}` : ""}
          </div>
          <div className="mt-1 font-editorial text-[15px] text-[#002147] truncate">
            {focus.degree}
          </div>
          <div className="text-[12px] text-stone-600">
            {focus.university} · <span className="tabular-nums">{fmtDate(focus.date)}</span>
            {(() => {
              const d = daysUntil(focus.date);
              if (d == null) return null;
              if (d < 0) return <span className="ml-1 text-stone-400">· {Math.abs(d)}d ago</span>;
              return <span className="ml-1 text-stone-500">· in {d}d</span>;
            })()}
          </div>
          {siblings.length > 0 && (
            <div className="mt-2 rounded-lg border border-stone-200 bg-stone-50 p-2">
              <div className="text-[10px] font-underground-sc tracking-wider text-stone-500 mb-1">
                Also on this date ({siblings.length})
              </div>
              <ul className="space-y-1" data-testid="deadline-timeline-siblings">
                {siblings.map((s) => (
                  <li key={s.id}>
                    <button
                      type="button"
                      onClick={() => setFocus(s)}
                      className="w-full text-left text-[12px] hover:bg-white rounded px-1.5 py-1"
                    >
                      <span
                        className="inline-block h-1.5 w-1.5 rounded-full mr-1.5 align-middle"
                        style={{ background: cfg.colour }}
                      />
                      <span className="text-[#002147] font-editorial">{s.degree}</span>
                      <span className="text-stone-500"> · {s.university}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {hint && (
            <Link
              to={`/wua-plus?intent=${hint.intent}`}
              className="mt-2 inline-flex items-center gap-1 text-[11px] font-underground-sc tracking-wider text-[#4C1D95] hover:underline"
              data-testid="deadline-timeline-wuaplus-hint"
            >
              <Sparkles className="h-3 w-3" /> {hint.label} <ArrowRight className="h-3 w-3" />
            </Link>
          )}
        </div>
        <button
          type="button"
          onClick={() => setFocus(null)}
          className="text-stone-400 hover:text-stone-800 p-1"
          aria-label="Close focus panel"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}


function ComparePanel({ a, b, onExit }) {
  // Both `a` and `b` are timeline events with `_degree` + `_uni` back-refs.
  // We pull tuition, offer rate, next upcoming deadline, and post-study
  // visa signal — the four numbers a family actually asks about when
  // stack-ranking two competing offers.
  const rows = [
    { key: "cost", label: "Annual intl. tuition",
      a: fmtMoney(courseTuitionUsd(a._degree) ?? a._uni?.tuition_intl_annual_usd),
      b: fmtMoney(courseTuitionUsd(b._degree) ?? b._uni?.tuition_intl_annual_usd),
      compareBigger: false },
    { key: "offer_rate", label: "Offer rate (admission odds)",
      a: fmtPct(a._degree?.admissions?.offer_rate),
      b: fmtPct(b._degree?.admissions?.offer_rate),
      compareBigger: true },
    { key: "next_deadline", label: "Next upcoming deadline",
      a: fmtNextDeadline(a._degree),
      b: fmtNextDeadline(b._degree),
      compareBigger: false },
    { key: "psw", label: "Post-study work window",
      a: fmtPSW(a._uni),
      b: fmtPSW(b._uni),
      compareBigger: false },
    { key: "duration", label: "Duration",
      a: a._degree?.duration_years ? `${a._degree.duration_years} yrs` : "—",
      b: b._degree?.duration_years ? `${b._degree.duration_years} yrs` : "—",
      compareBigger: false },
  ];

  return (
    <div
      className="mt-4 rounded-xl border border-[#4C1D95]/30 bg-white p-4"
      data-testid="deadline-timeline-compare-panel"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <GitCompare className="h-4 w-4 text-[#4C1D95]" />
          <div className="label-uppercase text-[#4C1D95]">Side-by-side offer breakdown</div>
        </div>
        <button
          type="button"
          onClick={onExit}
          data-testid="deadline-timeline-compare-close"
          className="text-[11px] font-underground-sc tracking-wider text-stone-400 hover:text-stone-600"
        >
          close
        </button>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-[13px] border-collapse">
          <thead>
            <tr className="border-b border-stone-200 text-left align-bottom">
              <th className="py-2 pr-3 font-medium text-stone-500 text-[11px] font-underground-sc tracking-wider">Metric</th>
              <th className="py-2 px-3 font-editorial text-[14px] text-[#002147] max-w-[260px]" data-testid="compare-col-a">
                <div className="truncate">{a.university}</div>
                <div className="text-[11px] text-stone-500 font-sans normal-case tracking-normal font-normal truncate">
                  {a.degree}
                </div>
              </th>
              <th className="py-2 px-3 font-editorial text-[14px] text-[#002147] max-w-[260px]" data-testid="compare-col-b">
                <div className="truncate">{b.university}</div>
                <div className="text-[11px] text-stone-500 font-sans normal-case tracking-normal font-normal truncate">
                  {b.degree}
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const aWin = compareWinner(r, "a");
              const bWin = compareWinner(r, "b");
              return (
                <tr
                  key={r.key}
                  data-testid={`compare-row-${r.key}`}
                  className="border-b border-stone-100"
                >
                  <td className="py-2 pr-3 text-stone-500 whitespace-nowrap">{r.label}</td>
                  <td className={"py-2 px-3 tabular-nums " + (aWin ? "text-emerald-700 font-medium" : "text-stone-900")}>
                    {r.a}{aWin && <span className="ml-1 text-emerald-600 text-[10px] font-underground-sc tracking-wider">better</span>}
                  </td>
                  <td className={"py-2 px-3 tabular-nums " + (bWin ? "text-emerald-700 font-medium" : "text-stone-900")}>
                    {r.b}{bWin && <span className="ml-1 text-emerald-600 text-[10px] font-underground-sc tracking-wider">better</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="mt-4 flex items-center gap-2 flex-wrap text-[12px]">
        {a.degree_id && (
          <Link
            to={`/degree/${a.degree_id}`}
            data-testid="compare-open-a"
            className="inline-flex items-center gap-1 rounded-full border border-stone-300 px-3 py-1 font-underground-sc tracking-wider text-[10px] text-stone-700 hover:bg-stone-100"
          >
            Open {a.university.split(" ").slice(0, 2).join(" ")}
          </Link>
        )}
        {b.degree_id && (
          <Link
            to={`/degree/${b.degree_id}`}
            data-testid="compare-open-b"
            className="inline-flex items-center gap-1 rounded-full border border-stone-300 px-3 py-1 font-underground-sc tracking-wider text-[10px] text-stone-700 hover:bg-stone-100"
          >
            Open {b.university.split(" ").slice(0, 2).join(" ")}
          </Link>
        )}
        <Link
          to={`/wua-plus?intent=offer-review`}
          data-testid="compare-open-wua-plus"
          className="inline-flex items-center gap-1 rounded-full bg-[#4C1D95] px-3 py-1 font-underground-sc tracking-wider text-[10px] text-white hover:bg-[#3B0764] ml-auto"
        >
          <Sparkles className="h-3 w-3" /> Decide with a WUA+ counsellor
        </Link>
      </div>
    </div>
  );
}

// ---- ComparePanel helpers ---------------------------------------------
function fmtMoney(v) {
  if (v == null || Number.isNaN(Number(v))) return "—";
  const n = Number(v);
  return "$" + Math.round(n).toLocaleString("en-US") + "/yr";
}
function fmtPct(v) {
  if (v == null || Number.isNaN(Number(v))) return "—";
  return `${Math.round(Number(v) * 100)}%`;
}
function fmtNextDeadline(deg) {
  const app = deg?.application || {};
  const fields = ["opens", "closes", "decision_by", "deposit_deadline"];
  const now = Date.now();
  const upcoming = fields
    .map((f) => ({ field: f, date: parseISODate(app[f]) }))
    .filter((r) => r.date && r.date.getTime() >= now)
    .sort((x, y) => x.date - y.date);
  if (!upcoming.length) return "—";
  const label = { opens: "opens", closes: "closes", decision_by: "decision", deposit_deadline: "deposit" };
  const u = upcoming[0];
  return `${fmtDate(u.date)} (${label[u.field]})`;
}
function fmtPSW(uni) {
  if (!uni) return "—";
  const yrs = uni.graduate_visa_years;
  if (yrs == null) return uni.work_rights_after || "—";
  return `${yrs} yrs · ${uni.work_rights_after || "PSW"}`;
}
function compareWinner(row, side) {
  // "Better" is only awarded when we can compare numerically. For tuition
  // (compareBigger=false) lower wins. For offer_rate (compareBigger=true)
  // higher wins. Anything non-numeric or a tie → no badge.
  const parse = (s) => {
    if (typeof s !== "string") return null;
    const n = parseFloat(s.replace(/[^0-9.-]/g, ""));
    return Number.isFinite(n) ? n : null;
  };
  const a = parse(row.a);
  const b = parse(row.b);
  if (a == null || b == null || a === b) return false;
  const winner = row.compareBigger ? (a > b ? "a" : "b") : (a < b ? "a" : "b");
  return winner === side;
}
